/**
 * Rubriques de construction de la barre d'outils (remontées, pistes, puis
 * les rubriques de bâtiments du catalogue) : libellé, pictogramme, couleur,
 * et rubrique ouverte dans le menu de construction.
 */
import { signal } from '@preact/signals';
import { BUILDINGS, BUILDING_GROUPS, LIFTS, type BuildingGroup, type BuildingKind, type LiftKind } from '@snowchain/shared';
import { PgBed, PgChair, PgChalet, PgDrag, PgFood, PgLift, PgPiste, PgSkier, PgSnowflake, type IconType } from './icons';
import { hud } from './context';
import { ensureThumbs, thumbs } from '../render/thumbs';

export type BuildCat = 'lift' | 'piste' | BuildingGroup;

export interface BuildCatMeta {
  id: BuildCat;
  label: string;
  icon: IconType;
  color: string;
}

const GROUP_ICON: Record<BuildingGroup, IconType> = {
  accueil: PgChalet,
  hebergement: PgBed,
  restauration: PgFood,
  services: PgSkier,
  exploitation: PgSnowflake,
};

export const BUILD_CATS: BuildCatMeta[] = [
  { id: 'lift', label: 'Remontées mécaniques', icon: PgChair, color: 'var(--cat-lift)' },
  { id: 'piste', label: 'Pistes', icon: PgPiste, color: 'var(--cat-piste)' },
  ...(Object.keys(BUILDING_GROUPS) as BuildingGroup[]).map((g) => ({ id: g, label: BUILDING_GROUPS[g], icon: GROUP_ICON[g] ?? PgChalet, color: `var(--cat-${g})` })),
];

export function catMeta(id: BuildCat): BuildCatMeta {
  return BUILD_CATS.find((c) => c.id === id) ?? (BUILD_CATS[0] as BuildCatMeta);
}

/** Rubrique d'un bâtiment (couleur de sa fiche et de sa vignette). */
export function buildingCat(kind: BuildingKind): BuildCatMeta {
  return catMeta(BUILDINGS[kind].group);
}

export const LIFT_ICON: Record<LiftKind, IconType> = { teleski: PgDrag, telesiege: PgChair, telecabine: PgLift };

export function liftIcon(kind: LiftKind): IconType {
  return LIFT_ICON[kind] ?? PgLift;
}

/** Les rubriques sans aucun bâtiment au catalogue ne sont pas proposées. */
export function catHasItems(id: BuildCat): boolean {
  if (id === 'lift') return Object.keys(LIFTS).length > 0;
  if (id === 'piste') return true;
  return (Object.keys(BUILDINGS) as BuildingKind[]).some((k) => BUILDINGS[k].group === id);
}

/** Rubrique ouverte dans le menu de construction (la dernière choisie). */
export const buildCat = signal<BuildCat>('lift');

/**
 * Vignette 3D d'un type (`b:<bâtiment>`, `l:<remontée>`), ou null tant qu'elle
 * n'est pas prête. Le premier appel lance le rendu de toutes les vignettes.
 */
export function thumbUrl(key: string): string | null {
  const engine = hud.engine;
  if (engine) ensureThumbs(engine.ctx.renderer, engine.lib);
  return thumbs.value[key] ?? null;
}
